import React from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Paper,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import AccountTreeRoundedIcon from "@mui/icons-material/AccountTreeRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import BusinessCenterRoundedIcon from "@mui/icons-material/BusinessCenterRounded";
import CampaignRoundedIcon from "@mui/icons-material/CampaignRounded";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import DashboardCustomizeRoundedIcon from "@mui/icons-material/DashboardCustomizeRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import FactCheckRoundedIcon from "@mui/icons-material/FactCheckRounded";
import HandshakeRoundedIcon from "@mui/icons-material/HandshakeRounded";
import HelpOutlineRoundedIcon from "@mui/icons-material/HelpOutlineRounded";
import IosShareRoundedIcon from "@mui/icons-material/IosShareRounded";
import LinkRoundedIcon from "@mui/icons-material/LinkRounded";
import PaymentsRoundedIcon from "@mui/icons-material/PaymentsRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import PersonAddAlt1RoundedIcon from "@mui/icons-material/PersonAddAlt1Rounded";
import StorefrontRoundedIcon from "@mui/icons-material/StorefrontRounded";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";

import { affiliatesApi } from "../../api/affiliates";
import { getPublicSellerProgram } from "../../api/publicSellerProgram";
import SellerIncomeSimulator from "./SellerIncomeSimulator";

const BRAND = {
  orange: "#F35A3A",
  orangeDark: "#DF4728",
  dark: "#19242F",
  green: "#2A9D67",
};

const WHATSAPP_URL = "https://wa.me/message/HOYXZTTFNXNZH1";

const sellerGuideLinks = [
  { id: "mi-enlace-referidos", label: "Mi enlace" },
  { id: "vendedores-servicios", label: "Servicios y precios" },
  { id: "vendedores-simulador", label: "Simulador" },
  { id: "vendedores-ayuda", label: "Reglas y ayuda" },
];

const validCommissionConditions = [
  {
    title: "Registro atribuido correctamente",
    text: "El responsable del negocio debe abrir el enlace personal del referente y crear su propia cuenta. El alta sin ese origen no se reasigna manualmente desde esta sección.",
  },
  {
    title: "Proyecto con mensualidad activa",
    text: "La cuenta debe tener un proyecto con facturación activa y un importe mensual mayor que cero. Una cuenta creada o una prueba, por sí solas, no generan comisión.",
  },
  {
    title: "Pago confirmado y elegible",
    text: "La comisión se registra sobre un pago aprobado por el sistema, usando la política que estaba vigente para ese movimiento y evitando duplicar el mismo pago.",
  },
  {
    title: "Sin devolución ni contracargo",
    text: "Si el pago se devuelve, se cancela o recibe un contracargo, la comisión relacionada se cancela conservando el historial del movimiento.",
  },
];

const panelTrackingItems = [
  "Referidos directos y personas incorporadas por la red.",
  "Proyectos asociados, servicios activos, mensualidad y próxima fecha disponible.",
  "Comisión estimada, registrada como disponible y ya pagada, separada por moneda.",
  "Relación directa o de red, profundidad y porcentaje aplicado a cada proyecto.",
];

const pitchExamples = [
  {
    audience: "Comercio con productos",
    opening: "Podés mostrar tus productos, recibir pedidos y ordenar la gestión desde un mismo proyecto.",
    focus: "Empezá por el problema actual: catálogo desactualizado, stock difícil de seguir, pedidos dispersos o cobros sin trazabilidad.",
  },
  {
    audience: "Profesional o consultora",
    opening: "Podés presentar tus servicios, centralizar consultas y sumar agenda o herramientas de seguimiento cuando las necesites.",
    focus: "Mostrá primero cómo la página explica la propuesta y después sólo los módulos que resuelven su forma real de trabajar.",
  },
  {
    audience: "Emprendimiento que vende online",
    opening: "Podés empezar con una presencia clara y activar catálogo, pedidos, pagos o gestión a medida que crece la operación.",
    focus: "Evitá prometer una solución cerrada: relevá qué vende, cómo cobra, cómo entrega y quién administra la información.",
  },
  {
    audience: "Negocio con turnos o atención",
    opening: "Podés ordenar la presentación del negocio y sumar agenda, clientes y seguimiento sin perder el contacto personal.",
    focus: "Explicá el recorrido completo del cliente y verificá qué parte necesita digitalizar primero.",
  },
];

const sellerEssentials = [
  {
    icon: StorefrontRoundedIcon,
    label: "Qué ofrecés",
    title: "Los servicios vigentes de WebProfit.",
    text: "Presentás la plataforma con el catálogo y los precios publicados, sin inventar paquetes ni condiciones.",
  },
  {
    icon: LinkRoundedIcon,
    label: "Cómo obtenés tu enlace",
    title: "Sale de tu propia cuenta.",
    text: "Creás tu usuario, iniciás sesión y WebProfit muestra el enlace personal existente para copiar o compartir.",
  },
  {
    icon: PaymentsRoundedIcon,
    label: "Cuándo genera comisión",
    title: "Cuando existe un pago elegible.",
    text: "El negocio debe registrarse desde tu enlace y luego tener un pago válido y confirmado según la política vigente.",
  },
];

function scrollToSellerSection(id) {
  if (typeof document === "undefined") return;
  document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
}

function finiteNumber(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function percent(value) {
  const parsed = finiteNumber(value);
  if (parsed === null) return "";

  return `${new Intl.NumberFormat("es-AR", {
    maximumFractionDigits: 2,
  }).format(parsed)}%`;
}

function money(value, currency) {
  const parsed = finiteNumber(value);
  if (parsed === null) return "Consultar";
  if (parsed === 0) return "Sin costo";

  try {
    return new Intl.NumberFormat("es-AR", {
      style: "currency",
      currency: String(currency || "ARS").toUpperCase(),
      maximumFractionDigits: 0,
    }).format(parsed);
  } catch {
    return `${new Intl.NumberFormat("es-AR", {
      maximumFractionDigits: 0,
    }).format(parsed)} ${String(currency || "ARS").toUpperCase()}`;
  }
}

function dateLabel(value) {
  const parsed = value ? new Date(value) : null;
  if (!parsed || Number.isNaN(parsed.getTime())) return "";

  return new Intl.DateTimeFormat("es-AR", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  }).format(parsed);
}

function ServiceCard({ service }) {
  const features = Array.isArray(service?.features)
    ? service.features.filter(Boolean).slice(0, 2)
    : [];

  return (
    <Paper
      elevation={0}
      sx={{
        height: "100%",
        p: { xs: 1.4, sm: 2.2, md: 2.4 },
        borderRadius: { xs: 3, sm: 4 },
        border: "1px solid rgba(25,36,47,.09)",
        bgcolor: service?.recommended ? "#FFF7F3" : "#fff",
        boxShadow: "0 16px 34px rgba(25,36,47,.055)",
      }}
    >
      <Stack direction="row" justifyContent="space-between" spacing={1.5}>
        <Box sx={{ minWidth: 0 }}>
          <Typography
            sx={{
              color: BRAND.dark,
              fontWeight: 900,
              lineHeight: 1.2,
              fontSize: "1rem",
            }}
          >
            {service?.name || "Servicio WebProfit"}
          </Typography>
          <Typography
            sx={{
              mt: 0.7,
              display: { xs: "none", sm: "block" },
              color: "rgba(25,36,47,.62)",
              fontSize: ".84rem",
              lineHeight: 1.5,
            }}
          >
            {service?.description || "Una solución configurable para la operación del negocio."}
          </Typography>
        </Box>
        {service?.recommended ? (
          <Chip
            label="Recomendado"
            size="small"
            sx={{
              flexShrink: 0,
              display: { xs: "none", sm: "inline-flex" },
              bgcolor: "rgba(243,90,58,.11)",
              color: "#C83E21",
              fontWeight: 850,
            }}
          />
        ) : null}
      </Stack>

      <Typography sx={{ mt: { xs: .9, sm: 2 }, color: BRAND.orange, fontWeight: 950, fontSize: { xs: ".98rem", sm: "1.12rem" } }}>
        {money(service?.monthlyPrice, service?.currency)}
        {finiteNumber(service?.monthlyPrice) > 0 ? (
          <Box component="span" sx={{ color: "rgba(25,36,47,.48)", fontWeight: 750, fontSize: ".76rem" }}>
            {" "}/ mes
          </Box>
        ) : null}
      </Typography>

      {features.length ? (
        <Stack spacing={0.7} sx={{ mt: 1.6, display: { xs: "none", sm: "flex" } }}>
          {features.map((feature) => (
            <Stack key={feature} direction="row" spacing={0.8} alignItems="flex-start">
              <CheckCircleRoundedIcon sx={{ mt: 0.05, color: BRAND.green, fontSize: 17 }} />
              <Typography sx={{ color: "rgba(25,36,47,.65)", fontSize: ".8rem", lineHeight: 1.45 }}>
                {feature}
              </Typography>
            </Stack>
          ))}
        </Stack>
      ) : null}
    </Paper>
  );
}

function SellerEssentials() {
  return (
    <Box
      sx={{
        mt: { xs: 2, sm: 3 },
        display: "grid",
        gridTemplateColumns: { xs: "1fr", sm: "repeat(3,minmax(0,1fr))" },
        gap: { xs: .8, sm: 1.4 },
      }}
    >
      {sellerEssentials.map(({ icon: Icon, label, title, text }) => (
        <Paper
          key={label}
          elevation={0}
          sx={{
            p: { xs: 1.35, sm: 2.1 },
            minWidth: 0,
            borderRadius: 3.5,
            border: "1px solid rgba(25,36,47,.09)",
            bgcolor: "#fff",
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <Box
              sx={{
                width: 36,
                height: 36,
                flexShrink: 0,
                borderRadius: 2.4,
                display: "grid",
                placeItems: "center",
                bgcolor: "rgba(243,90,58,.1)",
                color: BRAND.orange,
              }}
            >
              <Icon sx={{ fontSize: 20 }} />
            </Box>
            <Typography
              sx={{
                color: "rgba(25,36,47,.5)",
                fontSize: ".69rem",
                fontWeight: 900,
                letterSpacing: ".08em",
                textTransform: "uppercase",
              }}
            >
              {label}
            </Typography>
          </Stack>
          <Typography sx={{ mt: { xs: .75, sm: 1.35 }, color: BRAND.dark, fontWeight: 950, fontSize: { xs: ".9rem", sm: ".96rem" }, lineHeight: 1.28 }}>
            {title}
          </Typography>
          <Typography sx={{ mt: 0.65, display: { xs: "none", sm: "block" }, color: "rgba(25,36,47,.61)", fontSize: ".8rem", lineHeight: 1.5 }}>
            {text}
          </Typography>
        </Paper>
      ))}
    </Box>
  );
}

function CommissionCard({ program, loading }) {
  const status = String(program?.status || "incomplete").toLowerCase();
  const isActive = status === "active" && program?.enabled === true;
  const isPaused = status === "paused" || program?.enabled === false;
  const directPercent = finiteNumber(program?.directCommissionPercent);
  const levelBehavior = program?.levelBehavior || null;
  const retentionPercent = finiteNumber(levelBehavior?.retentionPercentPerLevel);
  const maximumPercent = finiteNumber(levelBehavior?.maximumTotalCommissionPercent);
  const effectiveFrom = dateLabel(program?.effectiveFrom);

  return (
    <Paper
      elevation={0}
      sx={{
        position: "relative",
        overflow: "hidden",
        p: { xs: 2, sm: 3.2, md: 3.6 },
        borderRadius: 5,
        color: "#fff",
        background: "linear-gradient(145deg, #19242F 0%, #253A49 70%, #2C4658 100%)",
        boxShadow: "0 28px 65px rgba(25,36,47,.24)",
        "&::before": {
          content: '\"\"',
          position: "absolute",
          width: 230,
          height: 230,
          borderRadius: "50%",
          right: -80,
          top: -100,
          bgcolor: "rgba(243,90,58,.24)",
        },
      }}
    >
      <Stack sx={{ position: "relative", zIndex: 1 }} spacing={{ xs: 1.5, sm: 2.4 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1.5}>
          <Box
            sx={{
              width: { xs: 42, sm: 52 },
              height: { xs: 42, sm: 52 },
              borderRadius: 3.2,
              bgcolor: "rgba(255,255,255,.11)",
              display: "grid",
              placeItems: "center",
            }}
          >
            <AccountTreeRoundedIcon sx={{ fontSize: 29 }} />
          </Box>
          <Chip
            label={isActive ? "Programa activo" : isPaused ? "Programa pausado" : "Condiciones en revisión"}
            size="small"
            sx={{
              bgcolor: isActive ? "rgba(42,157,103,.2)" : "rgba(255,255,255,.12)",
              color: isActive ? "#A9F0CE" : "rgba(255,255,255,.78)",
              fontWeight: 900,
            }}
          />
        </Stack>

        {loading ? (
          <Stack direction="row" alignItems="center" spacing={1.2} sx={{ minHeight: 116 }}>
            <CircularProgress size={24} sx={{ color: "#fff" }} />
            <Typography sx={{ color: "rgba(255,255,255,.7)", fontWeight: 750 }}>
              Consultando condiciones vigentes…
            </Typography>
          </Stack>
        ) : isActive && directPercent !== null ? (
          <Box>
            <Typography sx={{ color: "#FFB49F", fontWeight: 900, fontSize: ".78rem", letterSpacing: ".1em", textTransform: "uppercase" }}>
              Comisión directa vigente
            </Typography>
            <Typography sx={{ mt: 0.8, fontWeight: 950, letterSpacing: "-.055em", lineHeight: 1, fontSize: { xs: "2.8rem", sm: "4.4rem" } }}>
              {percent(directPercent)}
            </Typography>
            <Typography sx={{ mt: 1.25, display: { xs: "none", sm: "block" }, color: "rgba(255,255,255,.72)", lineHeight: 1.6 }}>
              Sobre pagos elegibles de negocios registrados correctamente desde tu enlace personal.
            </Typography>
          </Box>
        ) : (
          <Box sx={{ minHeight: 116 }}>
            <Typography sx={{ fontWeight: 950, letterSpacing: "-.035em", fontSize: "1.65rem", lineHeight: 1.15 }}>
              {isPaused ? "Las nuevas comisiones están temporalmente pausadas." : "Las condiciones se confirman antes de empezar."}
            </Typography>
            <Typography sx={{ mt: 1.3, color: "rgba(255,255,255,.7)", lineHeight: 1.6 }}>
              No mostramos porcentajes estimados ni valores desactualizados. El equipo puede informarte el estado comercial actual.
            </Typography>
          </Box>
        )}

        {isActive && levelBehavior ? (
          <Accordion
            disableGutters
            elevation={0}
            sx={{
              borderRadius: "18px !important",
              bgcolor: "rgba(255,255,255,.075)",
              color: "#fff",
              border: "1px solid rgba(255,255,255,.1)",
              overflow: "hidden",
              "&::before": { display: "none" },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreRoundedIcon sx={{ color: "rgba(255,255,255,.72)" }} />}
              sx={{ minHeight: { xs: 44, sm: 48 }, px: 1.8, "& .MuiAccordionSummary-content": { my: { xs: .8, sm: 1.1 } } }}
            >
              <Typography sx={{ fontWeight: 900, fontSize: ".86rem" }}>
                Ver cómo funcionan los niveles
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 1.8, pt: 0, pb: 1.7 }}>
              <Typography sx={{ color: "rgba(255,255,255,.67)", fontSize: ".8rem", lineHeight: 1.55 }}>
                {retentionPercent !== null
                  ? `Cada nivel conserva ${percent(retentionPercent)} del derecho anterior. `
                  : "Cada nivel se calcula con la política vigente. "}
                Los referentes superiores reciben sólo la diferencia
                {maximumPercent !== null ? ` y el total nunca supera ${percent(maximumPercent)}.` : "."}
              </Typography>
            </AccordionDetails>
          </Accordion>
        ) : null}

        <Typography sx={{ color: "rgba(255,255,255,.48)", fontSize: ".75rem", lineHeight: 1.45 }}>
          {effectiveFrom ? `Condiciones vigentes desde el ${effectiveFrom}. ` : ""}
          Registrarse o crear una cuenta, por sí solo, no genera una comisión.
        </Typography>
      </Stack>
    </Paper>
  );
}


async function copyToClipboard(value) {
  const text = String(value || "").trim();
  if (!text) throw new Error("No hay un enlace para copiar.");

  if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }

  if (typeof document === "undefined") {
    throw new Error("El portapapeles no está disponible.");
  }

  const field = document.createElement("textarea");
  field.value = text;
  field.setAttribute("readonly", "");
  field.style.position = "fixed";
  field.style.opacity = "0";
  document.body.appendChild(field);
  field.select();
  const copied = document.execCommand("copy");
  document.body.removeChild(field);

  if (!copied) throw new Error("No se pudo copiar el enlace.");
}

function ReferralJourneyCard({
  isAuthenticated,
  authLoading,
  referralCode,
  onJoinClick,
}) {
  const [affiliate, setAffiliate] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState("");
  const [feedback, setFeedback] = React.useState("");
  const [requestVersion, setRequestVersion] = React.useState(0);

  React.useEffect(() => {
    let active = true;

    if (authLoading || !isAuthenticated) {
      setAffiliate(null);
      setLoading(false);
      setError("");
      return () => {
        active = false;
      };
    }

    setLoading(true);
    setError("");
    setFeedback("");

    affiliatesApi
      .getSummary()
      .then((response) => {
        if (active) setAffiliate(response?.affiliate || null);
      })
      .catch((requestError) => {
        if (active) {
          setAffiliate(null);
          setError(requestError?.message || "No se pudo cargar tu enlace personal.");
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [authLoading, isAuthenticated, requestVersion]);

  const affiliateLink = String(affiliate?.link || "").trim();
  const affiliateCode = String(affiliate?.code || "").trim();

  const handleCopy = async () => {
    try {
      await copyToClipboard(affiliateLink);
      setFeedback("Enlace copiado. Ya podés pegarlo en WhatsApp, redes o un mensaje.");
    } catch (copyError) {
      setFeedback(copyError?.message || "No se pudo copiar. Seleccioná el enlace manualmente.");
    }
  };

  const handleShare = async () => {
    if (!affiliateLink) return;

    if (typeof navigator !== "undefined" && navigator.share) {
      try {
        await navigator.share({
          title: "WebProfit para tu negocio",
          text: "Te comparto WebProfit para que crees la cuenta de tu negocio desde mi enlace personal.",
          url: affiliateLink,
        });
        setFeedback("Enlace compartido.");
        return;
      } catch (shareError) {
        if (shareError?.name === "AbortError") return;
      }
    }

    await handleCopy();
  };

  const guestHasReferral = Boolean(referralCode);

  return (
    <Paper
      id="mi-enlace-referidos"
      elevation={0}
      sx={{
        mt: { xs: 3, sm: 4.5, md: 6 },
        p: { xs: 1.7, sm: 3, md: 4 },
        borderRadius: 5,
        border: "1px solid rgba(243,90,58,.18)",
        bgcolor: "#fff",
        boxShadow: "0 22px 55px rgba(25,36,47,.08)",
        scrollMarginTop: 96,
      }}
    >
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "minmax(0,1fr) minmax(320px,.9fr)" },
          gap: { xs: 1.8, sm: 3, md: 5 },
          alignItems: "center",
        }}
      >
        <Box>
          <Chip
            icon={isAuthenticated ? <LinkRoundedIcon /> : <PersonAddAlt1RoundedIcon />}
            label={
              authLoading
                ? "Comprobando tu sesión"
                : isAuthenticated
                  ? "Tu recorrido real de referidos"
                  : guestHasReferral
                    ? "Invitación de un referente detectada"
                    : "Primero creá tu cuenta"
            }
            sx={{
              bgcolor: isAuthenticated ? "rgba(42,157,103,.11)" : "rgba(243,90,58,.1)",
              color: isAuthenticated ? "#187A4C" : "#C83E21",
              fontWeight: 900,
              "& .MuiChip-icon": { color: "inherit" },
            }}
          />

          <Typography
            sx={{
              mt: 2,
              color: BRAND.dark,
              fontWeight: 950,
              letterSpacing: "-.04em",
              lineHeight: 1.08,
              fontSize: { xs: "1.42rem", sm: "2.05rem" },
            }}
          >
            {isAuthenticated
              ? "Tu enlace personal está listo para compartir."
              : guestHasReferral
                ? "Este acceso ya conserva la referencia de quien te invitó."
                : "Creá tu cuenta para obtener tu enlace personal."}
          </Typography>

          <Typography sx={{ mt: { xs: .8, sm: 1.4 }, color: "rgba(25,36,47,.64)", lineHeight: { xs: 1.45, sm: 1.7 }, fontSize: { xs: ".84rem", sm: "1rem" } }}>
            {isAuthenticated
              ? "Copialo o compartilo con el responsable del negocio. El alta debe empezar desde ese enlace para conservar la referencia."
              : guestHasReferral
                ? `El código ${referralCode} se conservará al registrarte. Completá vos mismo los datos de la cuenta y confirmá tu email.`
                : "Con la sesión iniciada, esta sección muestra el enlace existente de tu usuario para copiarlo o compartirlo."}
          </Typography>
        </Box>

        <Box>
          {authLoading ? (
            <Stack direction="row" spacing={1.2} alignItems="center" sx={{ minHeight: 120 }}>
              <CircularProgress size={24} sx={{ color: BRAND.orange }} />
              <Typography sx={{ color: "rgba(25,36,47,.62)", fontWeight: 750 }}>
                Revisando si ya tenés una cuenta activa…
              </Typography>
            </Stack>
          ) : isAuthenticated ? (
            loading ? (
              <Stack direction="row" spacing={1.2} alignItems="center" sx={{ minHeight: 120 }}>
                <CircularProgress size={24} sx={{ color: BRAND.orange }} />
                <Typography sx={{ color: "rgba(25,36,47,.62)", fontWeight: 750 }}>
                  Cargando tu enlace personal existente…
                </Typography>
              </Stack>
            ) : error || !affiliateLink ? (
              <Alert
                severity="warning"
                action={(
                  <Button color="inherit" size="small" onClick={() => setRequestVersion((value) => value + 1)}>
                    Reintentar
                  </Button>
                )}
              >
                {error || "Tu cuenta está activa, pero el enlace personal no está disponible en este momento."}
              </Alert>
            ) : (
              <Stack spacing={1.6}>
                {affiliateCode ? (
                  <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                    <Typography sx={{ color: "rgba(25,36,47,.5)", fontWeight: 800, fontSize: ".78rem" }}>
                      TU CÓDIGO
                    </Typography>
                    <Chip label={affiliateCode} size="small" sx={{ bgcolor: "#F2F4F7", color: BRAND.dark, fontWeight: 950 }} />
                  </Stack>
                ) : null}

                <Box
                  role="textbox"
                  aria-label="Enlace personal de referidos"
                  tabIndex={0}
                  sx={{
                    p: 1.8,
                    borderRadius: 3,
                    bgcolor: "#F6F7F9",
                    border: "1px solid rgba(25,36,47,.09)",
                    color: BRAND.dark,
                    fontSize: ".84rem",
                    lineHeight: 1.55,
                    wordBreak: "break-all",
                    userSelect: "text",
                  }}
                >
                  {affiliateLink}
                </Box>

                <Stack direction={{ xs: "row", sm: "row" }} spacing={{ xs: .7, sm: 1.1 }}>
                  <Button
                    onClick={handleCopy}
                    variant="contained"
                    startIcon={<ContentCopyRoundedIcon />}
                    fullWidth
                    sx={{
                      minHeight: { xs: 44, sm: 49 },
                      borderRadius: 999,
                      bgcolor: BRAND.orange,
                      textTransform: "none",
                      fontWeight: 900,
                      "&:hover": { bgcolor: BRAND.orangeDark },
                    }}
                  >
                    Copiar enlace
                  </Button>
                  <Button
                    onClick={handleShare}
                    variant="outlined"
                    startIcon={<IosShareRoundedIcon />}
                    fullWidth
                    sx={{
                      minHeight: { xs: 44, sm: 49 },
                      borderRadius: 999,
                      borderColor: "rgba(25,36,47,.2)",
                      color: BRAND.dark,
                      textTransform: "none",
                      fontWeight: 900,
                    }}
                  >
                    Compartir
                  </Button>
                </Stack>

                {feedback ? (
                  <Typography role="status" aria-live="polite" sx={{ color: feedback.startsWith("No se") ? "#A33B24" : "#187A4C", fontSize: ".8rem", fontWeight: 800 }}>
                    {feedback}
                  </Typography>
                ) : null}
              </Stack>
            )
          ) : (
            <Stack spacing={1.5}>
              <Button
                onClick={onJoinClick}
                variant="contained"
                size="large"
                endIcon={<ArrowForwardRoundedIcon />}
                sx={{
                  minHeight: 54,
                  borderRadius: 999,
                  bgcolor: BRAND.orange,
                  textTransform: "none",
                  fontWeight: 900,
                  "&:hover": { bgcolor: BRAND.orangeDark },
                }}
              >
                {guestHasReferral ? "Crear la cuenta del negocio" : "Crear mi cuenta como referente"}
              </Button>
              <Typography sx={{ display: { xs: "none", sm: "block" }, color: "rgba(25,36,47,.52)", fontSize: ".79rem", lineHeight: 1.55 }}>
                {guestHasReferral
                  ? "El registro enviará el código recibido al backend, que validará y aplicará la referencia real."
                  : "Al iniciar sesión podrás compartir tu enlace desde celular o escritorio."}
              </Typography>
            </Stack>
          )}
        </Box>
      </Box>
    </Paper>
  );
}


function SellerGuideNavigation() {
  return (
    <Paper
      component="nav"
      aria-label="Guía del programa de vendedores"
      elevation={0}
      sx={{
        mt: { xs: 3, md: 5 },
        p: 1.1,
        display: { xs: "none", sm: "block" },
        borderRadius: 999,
        border: "1px solid rgba(25,36,47,.09)",
        bgcolor: "rgba(255,255,255,.9)",
        boxShadow: "0 12px 32px rgba(25,36,47,.045)",
        overflowX: "auto",
      }}
    >
      <Stack direction="row" spacing={0.7} sx={{ width: "max-content", minWidth: "100%" }}>
        {sellerGuideLinks.map((item) => (
          <Button
            key={item.id}
            size="small"
            onClick={() => scrollToSellerSection(item.id)}
            aria-controls={item.id}
            sx={{
              minHeight: 40,
              px: 1.8,
              borderRadius: 999,
              color: BRAND.dark,
              textTransform: "none",
              fontWeight: 850,
              whiteSpace: "nowrap",
              "&:hover": { bgcolor: "rgba(243,90,58,.08)", color: BRAND.orangeDark },
            }}
          >
            {item.label}
          </Button>
        ))}
      </Stack>
    </Paper>
  );
}

function LevelCommissionGuide({ program, loading }) {
  const status = String(program?.status || "incomplete").toLowerCase();
  const active = status === "active" && program?.enabled === true;
  const behavior = program?.levelBehavior || null;
  const levels = Array.isArray(behavior?.levels)
    ? behavior.levels.filter((item) => finiteNumber(item?.directCommissionPercent) !== null).slice(0, 5)
    : [];
  const retention = finiteNumber(behavior?.retentionPercentPerLevel);
  const maximum = finiteNumber(behavior?.maximumTotalCommissionPercent);

  return (
    <Paper
      id="vendedores-comisiones"
      elevation={0}
      sx={{
        p: { xs: 2.5, sm: 3.2, md: 3.8 },
        borderRadius: 4.5,
        border: "1px solid rgba(25,36,47,.09)",
        bgcolor: "#fff",
        boxShadow: "0 18px 46px rgba(25,36,47,.055)",
        scrollMarginTop: 96,
      }}
    >
      <Stack direction={{ xs: "column", md: "row" }} spacing={3.5}>
        <Box sx={{ flex: "1 1 42%" }}>
          <Stack direction="row" spacing={1.2} alignItems="center">
            <Box sx={{ width: 44, height: 44, borderRadius: 2.8, bgcolor: "rgba(243,90,58,.1)", color: BRAND.orange, display: "grid", placeItems: "center" }}>
              <AccountTreeRoundedIcon />
            </Box>
            <Box>
              <Typography sx={{ color: BRAND.orange, fontWeight: 900, letterSpacing: ".09em", textTransform: "uppercase", fontSize: ".72rem" }}>
                Niveles y comisiones
              </Typography>
              <Typography sx={{ color: BRAND.dark, fontWeight: 950, fontSize: { xs: "1.35rem", sm: "1.6rem" }, lineHeight: 1.15 }}>
                Una política, distintos derechos según la profundidad.
              </Typography>
            </Box>
          </Stack>

          <Typography sx={{ mt: 2, color: "rgba(25,36,47,.64)", lineHeight: 1.68 }}>
            El nivel 1 corresponde al origen directo del negocio. Los niveles siguientes conservan una parte del derecho anterior y los referentes superiores reciben sólo la diferencia que les corresponde; no se acumulan porcentajes sin límite.
          </Typography>

          {loading ? (
            <Stack spacing={1} sx={{ mt: 2.2 }} aria-label="Cargando política de comisiones">
              <Skeleton variant="rounded" height={38} />
              <Skeleton variant="rounded" height={38} width="78%" />
            </Stack>
          ) : active && behavior ? (
            <Stack spacing={0.9} sx={{ mt: 2.2 }}>
              {retention !== null ? (
                <Typography sx={{ color: BRAND.dark, fontWeight: 850, fontSize: ".9rem" }}>
                  Cada nivel conserva {percent(retention)} del derecho anterior.
                </Typography>
              ) : null}
              {maximum !== null ? (
                <Typography sx={{ color: BRAND.dark, fontWeight: 850, fontSize: ".9rem" }}>
                  La suma total de la red no supera {percent(maximum)} del pago elegible.
                </Typography>
              ) : null}
              <Typography sx={{ color: "rgba(25,36,47,.55)", fontSize: ".8rem", lineHeight: 1.55 }}>
                Los porcentajes visibles son derechos de referencia calculados por la política vigente. El importe real depende del lugar de cada persona en la red y del pago confirmado.
              </Typography>
            </Stack>
          ) : (
            <Alert severity="info" sx={{ mt: 2.2, borderRadius: 3 }}>
              La política está pausada o incompleta. No mostramos porcentajes alternativos ni ejemplos con cifras inventadas.
            </Alert>
          )}
        </Box>

        <Divider flexItem orientation="vertical" sx={{ display: { xs: "none", md: "block" } }} />

        <Box sx={{ flex: "1 1 58%" }}>
          <Typography sx={{ color: BRAND.dark, fontWeight: 950, fontSize: "1rem" }}>
            Vista simple de la política vigente
          </Typography>
          {loading ? (
            <Box sx={{ mt: 1.7, display: "grid", gridTemplateColumns: { xs: "repeat(2,minmax(0,1fr))", sm: "repeat(3,minmax(0,1fr))" }, gap: 1 }}>
              {[0, 1, 2, 3, 4].map((item) => <Skeleton key={item} variant="rounded" height={92} />)}
            </Box>
          ) : active && levels.length ? (
            <Box sx={{ mt: 1.7, display: "grid", gridTemplateColumns: { xs: "repeat(2,minmax(0,1fr))", sm: "repeat(3,minmax(0,1fr))" }, gap: 1 }}>
              {levels.map((level) => (
                <Box
                  key={level.level}
                  sx={{
                    p: 1.6,
                    borderRadius: 3,
                    bgcolor: level.level === 1 ? "#FFF5F1" : "#F7F8FA",
                    border: level.level === 1 ? "1px solid rgba(243,90,58,.2)" : "1px solid rgba(25,36,47,.07)",
                  }}
                >
                  <Typography sx={{ color: "rgba(25,36,47,.48)", fontSize: ".7rem", fontWeight: 900, textTransform: "uppercase" }}>
                    Nivel {level.level}
                  </Typography>
                  <Typography sx={{ mt: 0.45, color: level.level === 1 ? BRAND.orangeDark : BRAND.dark, fontWeight: 950, fontSize: "1.28rem" }}>
                    {percent(level.directCommissionPercent)}
                  </Typography>
                  <Typography sx={{ mt: 0.25, color: "rgba(25,36,47,.5)", fontSize: ".69rem", lineHeight: 1.35 }}>
                    {level.level === 1 ? "Derecho directo" : "Derecho a esa profundidad"}
                  </Typography>
                </Box>
              ))}
            </Box>
          ) : (
            <Paper elevation={0} sx={{ mt: 1.7, p: 2.2, borderRadius: 3, bgcolor: "#F7F8FA", border: "1px dashed rgba(25,36,47,.16)" }}>
              <Typography sx={{ color: "rgba(25,36,47,.6)", lineHeight: 1.6 }}>
                La vista por niveles aparecerá cuando el sistema publique una política activa y completa.
              </Typography>
            </Paper>
          )}
        </Box>
      </Stack>
    </Paper>
  );
}

function ValidCommissionGuide() {
  return (
    <Box sx={{ mt: 2, display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(2,minmax(0,1fr))" }, gap: 1.5 }}>
      {validCommissionConditions.map((item, index) => (
        <Paper key={item.title} elevation={0} sx={{ p: 2.2, borderRadius: 3.5, border: "1px solid rgba(25,36,47,.08)", bgcolor: index === 2 ? "#F2FAF6" : "#fff" }}>
          <Stack direction="row" spacing={1.2} alignItems="flex-start">
            <Box sx={{ width: 34, height: 34, flexShrink: 0, borderRadius: 2.2, bgcolor: index === 2 ? BRAND.green : BRAND.dark, color: "#fff", display: "grid", placeItems: "center", fontWeight: 950, fontSize: ".8rem" }}>
              {index + 1}
            </Box>
            <Box>
              <Typography sx={{ color: BRAND.dark, fontWeight: 900, lineHeight: 1.25 }}>
                {item.title}
              </Typography>
              <Typography sx={{ mt: 0.6, color: "rgba(25,36,47,.61)", fontSize: ".84rem", lineHeight: 1.58 }}>
                {item.text}
              </Typography>
            </Box>
          </Stack>
        </Paper>
      ))}
    </Box>
  );
}

function TrackingGuide({ isAuthenticated, onJoinClick }) {
  return (
    <Paper
      id="vendedores-seguimiento"
      elevation={0}
      sx={{
        p: { xs: 2.5, sm: 3.2, md: 3.8 },
        borderRadius: 4.5,
        color: "#fff",
        background: "linear-gradient(145deg, #19242F 0%, #263B4A 100%)",
        boxShadow: "0 22px 55px rgba(25,36,47,.18)",
        scrollMarginTop: 96,
      }}
    >
      <Stack direction={{ xs: "column", md: "row" }} spacing={3.5} justifyContent="space-between" alignItems={{ xs: "stretch", md: "center" }}>
        <Box sx={{ maxWidth: 690 }}>
          <Stack direction="row" spacing={1.2} alignItems="center">
            <Box sx={{ width: 44, height: 44, flexShrink: 0, borderRadius: 2.8, bgcolor: "rgba(255,255,255,.1)", display: "grid", placeItems: "center" }}>
              <DashboardCustomizeRoundedIcon />
            </Box>
            <Box>
              <Typography sx={{ color: "#FFB49F", fontWeight: 900, letterSpacing: ".09em", textTransform: "uppercase", fontSize: ".72rem" }}>
                Seguimiento dentro del panel
              </Typography>
              <Typography sx={{ fontWeight: 950, fontSize: { xs: "1.4rem", sm: "1.75rem" }, lineHeight: 1.14 }}>
                La red se revisa con proyectos y movimientos reales.
              </Typography>
            </Box>
          </Stack>
          <Typography sx={{ mt: 1.8, color: "rgba(255,255,255,.7)", lineHeight: 1.65 }}>
            Dentro de un proyecto, en Configuración y la sección general, el panel muestra el enlace personal y el resumen comercial de la red. Las estimaciones no reemplazan a las comisiones registradas por pagos confirmados.
          </Typography>
          <Box sx={{ mt: 2.2, display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2,minmax(0,1fr))" }, gap: 1 }}>
            {panelTrackingItems.map((item) => (
              <Stack key={item} direction="row" spacing={0.9} alignItems="flex-start">
                <CheckCircleRoundedIcon sx={{ mt: 0.05, color: "#8BE0B6", fontSize: 18 }} />
                <Typography sx={{ color: "rgba(255,255,255,.76)", fontSize: ".82rem", lineHeight: 1.5 }}>
                  {item}
                </Typography>
              </Stack>
            ))}
          </Box>
        </Box>

        <Box sx={{ flexShrink: 0, width: { xs: "100%", md: 245 } }}>
          <Button
            component={isAuthenticated ? "a" : "button"}
            href={isAuthenticated ? "#/dashboard/proyectos" : undefined}
            onClick={isAuthenticated ? undefined : onJoinClick}
            variant="contained"
            fullWidth
            endIcon={<ArrowForwardRoundedIcon />}
            sx={{ minHeight: 52, borderRadius: 999, bgcolor: BRAND.orange, textTransform: "none", fontWeight: 900, "&:hover": { bgcolor: BRAND.orangeDark } }}
          >
            {isAuthenticated ? "Abrir mis proyectos" : "Crear cuenta para acceder"}
          </Button>
          <Typography sx={{ mt: 1.1, color: "rgba(255,255,255,.5)", textAlign: "center", fontSize: ".72rem", lineHeight: 1.45 }}>
            El detalle personal requiere una sesión iniciada.
          </Typography>
        </Box>
      </Stack>
    </Paper>
  );
}

function PitchGuide() {
  return (
    <Box id="vendedores-presentacion" sx={{ scrollMarginTop: 96 }}>
      <Stack direction="row" spacing={1.2} alignItems="center">
        <Box sx={{ width: 44, height: 44, borderRadius: 2.8, bgcolor: "rgba(243,90,58,.1)", color: BRAND.orange, display: "grid", placeItems: "center" }}>
          <CampaignRoundedIcon />
        </Box>
        <Box>
          <Typography sx={{ color: BRAND.orange, fontWeight: 900, letterSpacing: ".09em", textTransform: "uppercase", fontSize: ".72rem" }}>
            Cómo presentar WebProfit
          </Typography>
          <Typography sx={{ color: BRAND.dark, fontWeight: 950, fontSize: { xs: "1.4rem", sm: "1.75rem" }, lineHeight: 1.14 }}>
            Empezá por la necesidad, no por una lista de módulos.
          </Typography>
        </Box>
      </Stack>

      <Box sx={{ mt: 2.2, display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(2,minmax(0,1fr))" }, gap: 1.5 }}>
        {pitchExamples.map((example) => (
          <Paper key={example.audience} elevation={0} sx={{ p: 2.4, borderRadius: 3.8, border: "1px solid rgba(25,36,47,.08)", bgcolor: "#fff" }}>
            <Typography sx={{ color: BRAND.dark, fontWeight: 950, fontSize: "1rem" }}>
              {example.audience}
            </Typography>
            <Typography sx={{ mt: 1, color: BRAND.orangeDark, fontWeight: 850, fontSize: ".88rem", lineHeight: 1.55 }}>
              “{example.opening}”
            </Typography>
            <Typography sx={{ mt: 1, color: "rgba(25,36,47,.58)", fontSize: ".82rem", lineHeight: 1.58 }}>
              {example.focus}
            </Typography>
          </Paper>
        ))}
      </Box>
    </Box>
  );
}

function SellerFaq({ program, pricing, loading }) {
  const direct = finiteNumber(program?.directCommissionPercent);
  const active = program?.status === "active" && program?.enabled === true;
  const servicesAvailable = Array.isArray(pricing?.services) && pricing.services.length > 0;
  const effectiveFrom = dateLabel(program?.effectiveFrom);
  const items = [
    {
      question: "¿Crear una cuenta ya genera comisión?",
      answer: "No. La atribución comienza con el registro correcto, pero la comisión se registra únicamente cuando existe un proyecto con mensualidad activa y un pago confirmado y elegible.",
    },
    {
      question: "¿Qué porcentaje puedo comunicar?",
      answer: loading
        ? "La sección está consultando la política vigente. Esperá a que termine la carga antes de comunicar un porcentaje."
        : active && direct !== null
          ? `La comisión directa publicada actualmente es ${percent(direct)}${effectiveFrom ? `, vigente desde el ${effectiveFrom}` : ""}. Los niveles siguientes se calculan con la misma política y pueden cambiar en futuras versiones.`
          : "No hay una tasa pública activa y completa en este momento. Consultá al equipo antes de comunicar condiciones.",
    },
    {
      question: "¿Puedo prometer un ingreso mensual?",
      answer: "No. El simulador usa precios y porcentajes vigentes para construir escenarios, pero el resultado depende de clientes reales, pagos confirmados, permanencia, devoluciones y la posición efectiva dentro de la red.",
    },
    {
      question: "¿Dónde veo mis referidos y comisiones?",
      answer: "Con la sesión iniciada, abrí uno de tus proyectos y entrá en Configuración, sección General. Allí se muestra el enlace personal, la red, los proyectos asociados y los movimientos disponibles o pagados.",
    },
    {
      question: "¿Qué hago si faltan precios o condiciones?",
      answer: servicesAvailable
        ? "Usá únicamente los servicios y valores visibles en esta página. Si una necesidad no está cubierta, consultá al equipo antes de cotizarla."
        : "No completes la información con valores propios. El catálogo volverá a mostrarse cuando el sistema publique datos comerciales disponibles; mientras tanto, consultá al equipo.",
    },
    {
      question: "¿El vendedor debe crear la cuenta del cliente?",
      answer: "No. El responsable del negocio debe abrir el enlace personal y completar sus propios datos. El referente acompaña la elección y la puesta en marcha, pero no suplanta al cliente final.",
    },
  ];

  return (
    <Box id="vendedores-faq" sx={{ scrollMarginTop: 96 }}>
      <Stack direction="row" spacing={1.2} alignItems="center">
        <Box sx={{ width: 44, height: 44, borderRadius: 2.8, bgcolor: "rgba(42,157,103,.11)", color: BRAND.green, display: "grid", placeItems: "center" }}>
          <HelpOutlineRoundedIcon />
        </Box>
        <Box>
          <Typography sx={{ color: BRAND.green, fontWeight: 900, letterSpacing: ".09em", textTransform: "uppercase", fontSize: ".72rem" }}>
            Preguntas frecuentes
          </Typography>
          <Typography sx={{ color: BRAND.dark, fontWeight: 950, fontSize: { xs: "1.4rem", sm: "1.75rem" }, lineHeight: 1.14 }}>
            Respuestas breves para presentar el programa con transparencia.
          </Typography>
        </Box>
      </Stack>

      <Box sx={{ mt: 2.2 }}>
        {items.map((item, index) => (
          <Accordion
            key={item.question}
            disableGutters
            elevation={0}
            sx={{
              mb: 1,
              borderRadius: "16px !important",
              border: "1px solid rgba(25,36,47,.08)",
              bgcolor: "#fff",
              overflow: "hidden",
              "&::before": { display: "none" },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreRoundedIcon />}
              aria-controls={`seller-faq-${index}-content`}
              id={`seller-faq-${index}-header`}
              sx={{ px: { xs: 2, sm: 2.5 }, minHeight: 58, "& .MuiAccordionSummary-content": { my: 1.4 } }}
            >
              <Typography sx={{ color: BRAND.dark, fontWeight: 900, fontSize: ".94rem" }}>
                {item.question}
              </Typography>
            </AccordionSummary>
            <AccordionDetails id={`seller-faq-${index}-content`} sx={{ px: { xs: 2, sm: 2.5 }, pt: 0, pb: 2.2 }}>
              <Typography sx={{ color: "rgba(25,36,47,.62)", fontSize: ".86rem", lineHeight: 1.65 }}>
                {item.answer}
              </Typography>
            </AccordionDetails>
          </Accordion>
        ))}
      </Box>
    </Box>
  );
}

function SellerProgramDocumentation({ program, pricing, loading, isAuthenticated, onJoinClick }) {
  return (
    <Accordion
      id="vendedores-ayuda"
      disableGutters
      elevation={0}
      sx={{
        mt: { xs: 5, md: 6 },
        borderRadius: "24px !important",
        border: "1px solid rgba(25,36,47,.1)",
        bgcolor: "#fff",
        overflow: "hidden",
        scrollMarginTop: 96,
        boxShadow: "0 16px 42px rgba(25,36,47,.045)",
        "&::before": { display: "none" },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreRoundedIcon />}
        aria-controls="vendedores-ayuda-content"
        id="vendedores-ayuda-header"
        sx={{
          px: { xs: 2.2, sm: 3 },
          py: 0.6,
          minHeight: 76,
          "& .MuiAccordionSummary-content": { my: 1.5 },
        }}
      >
        <Stack direction="row" spacing={1.3} alignItems="center">
          <Box
            sx={{
              width: 44,
              height: 44,
              flexShrink: 0,
              borderRadius: 2.8,
              bgcolor: "rgba(42,157,103,.11)",
              color: BRAND.green,
              display: "grid",
              placeItems: "center",
            }}
          >
            <HelpOutlineRoundedIcon />
          </Box>
          <Box>
            <Typography sx={{ color: BRAND.dark, fontWeight: 950, fontSize: { xs: "1.05rem", sm: "1.2rem" } }}>
              Reglas, niveles y ayuda
            </Typography>
            <Typography sx={{ mt: 0.2, color: "rgba(25,36,47,.56)", fontSize: ".8rem", lineHeight: 1.45 }}>
              Abrí esta guía sólo si necesitás revisar condiciones, seguimiento o ejemplos para presentar el programa.
            </Typography>
          </Box>
        </Stack>
      </AccordionSummary>
      <AccordionDetails id="vendedores-ayuda-content" sx={{ px: { xs: 1.4, sm: 2.2 }, pt: 0.5, pb: { xs: 2, sm: 2.6 } }}>
        <Stack spacing={2.2}>
          <LevelCommissionGuide program={program} loading={loading} />

          <Paper elevation={0} sx={{ p: { xs: 2.2, sm: 3 }, borderRadius: 4, border: "1px solid rgba(42,157,103,.15)", bgcolor: "#F6FBF8" }}>
            <Stack direction="row" spacing={1.2} alignItems="center">
              <Box sx={{ width: 42, height: 42, flexShrink: 0, borderRadius: 2.6, bgcolor: BRAND.green, color: "#fff", display: "grid", placeItems: "center" }}>
                <FactCheckRoundedIcon />
              </Box>
              <Box>
                <Typography sx={{ color: BRAND.green, fontWeight: 900, letterSpacing: ".08em", textTransform: "uppercase", fontSize: ".7rem" }}>
                  Comisión válida
                </Typography>
                <Typography sx={{ color: BRAND.dark, fontWeight: 950, fontSize: { xs: "1.15rem", sm: "1.35rem" }, lineHeight: 1.18 }}>
                  Qué tiene que pasar antes de que se registre.
                </Typography>
              </Box>
            </Stack>
            <ValidCommissionGuide />
          </Paper>

          <TrackingGuide isAuthenticated={isAuthenticated} onJoinClick={onJoinClick} />

          <Paper elevation={0} sx={{ p: { xs: 2.2, sm: 3 }, borderRadius: 4, border: "1px solid rgba(25,36,47,.09)", bgcolor: "#fff" }}>
            <PitchGuide />
          </Paper>

          <Paper elevation={0} sx={{ p: { xs: 2, sm: 3 }, borderRadius: 4, border: "1px solid rgba(25,36,47,.09)", bgcolor: "#F7F8FA" }}>
            <SellerFaq program={program} pricing={pricing} loading={loading} />
          </Paper>
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}

export default function SellerProgramSection({
  onJoinClick,
  joinLabel = "Crear mi cuenta y empezar",
  isAuthenticated = false,
  authLoading = false,
  referralCode = "",
}) {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [requestVersion, setRequestVersion] = React.useState(0);

  React.useEffect(() => {
    let active = true;

    setLoading(true);
    setError("");

    getPublicSellerProgram()
      .then((response) => {
        if (active) setData(response || null);
      })
      .catch((requestError) => {
        if (active) {
          setData(null);
          setError(requestError?.message || "No pudimos consultar los datos comerciales vigentes.");
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [requestVersion]);

  const pricing = data?.pricing || null;
  const services = Array.isArray(pricing?.services) ? pricing.services.slice(0, 4) : [];
  const hiddenServices = Math.max(0, (pricing?.services?.length || 0) - services.length);

  return (
    <Box
      id="trabaja-con-webprofit"
      component="section"
      aria-labelledby="trabaja-con-webprofit-title"
      aria-busy={loading}
      sx={{
        position: "relative",
        py: { xs: 5.5, sm: 8, md: 12 },
        px: { xs: 0, md: 2 },
        scrollMarginTop: 88,
        bgcolor: "#F7F8FA",
        overflow: "hidden",
        "&::before": {
          content: '\"\"',
          position: "absolute",
          width: 420,
          height: 420,
          borderRadius: "50%",
          left: -240,
          top: 70,
          background: "radial-gradient(circle, rgba(243,90,58,.13), rgba(243,90,58,0) 70%)",
        },
      }}
    >
      <Container maxWidth="lg" sx={{ position: "relative" }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", lg: "minmax(0,1.1fr) minmax(340px,.9fr)" },
            gap: { xs: 2.5, sm: 4, lg: 7 },
            alignItems: "start",
          }}
        >
          <Box>
            <Chip
              icon={<BusinessCenterRoundedIcon />}
              label="Trabajá con WebProfit"
              sx={{
                bgcolor: "rgba(243,90,58,.1)",
                color: "#C83E21",
                fontWeight: 900,
                "& .MuiChip-icon": { color: "#C83E21" },
              }}
            />
            <Typography
              id="trabaja-con-webprofit-title"
              sx={{
                mt: { xs: 1.4, sm: 2.2 },
                maxWidth: 760,
                color: BRAND.dark,
                fontWeight: 950,
                letterSpacing: "-.05em",
                lineHeight: 1.02,
                fontSize: { xs: "1.88rem", sm: "2.65rem", md: "3.45rem" },
              }}
            >
              Recomendá WebProfit con tu enlace personal.
            </Typography>
            <Typography
              sx={{
                mt: { xs: 1, sm: 1.7 },
                maxWidth: 720,
                color: "rgba(25,36,47,.68)",
                fontSize: { xs: ".9rem", md: "1.12rem" },
                lineHeight: { xs: 1.48, sm: 1.65, md: 1.72 },
              }}
            >
              Compartís tu enlace, el negocio crea su cuenta y una comisión puede generarse cuando existe un pago elegible. Precios y porcentajes siempre salen de la configuración vigente.
            </Typography>

            <SellerEssentials />

            <Stack direction={{ xs: "column", sm: "row" }} spacing={{ xs: .8, sm: 1.4 }} sx={{ mt: { xs: 1.8, sm: 2.5 } }}>
              <Button
                onClick={isAuthenticated
                  ? () => document.getElementById("mi-enlace-referidos")?.scrollIntoView({ behavior: "smooth", block: "center" })
                  : onJoinClick}
                variant="contained"
                size="large"
                endIcon={<ArrowForwardRoundedIcon />}
                sx={{
                  minHeight: { xs: 48, sm: 54 },
                  px: { xs: 2.4, sm: 3.1 },
                  borderRadius: 999,
                  bgcolor: BRAND.orange,
                  textTransform: "none",
                  fontWeight: 900,
                  boxShadow: "0 14px 30px rgba(243,90,58,.26)",
                  "&:hover": { bgcolor: BRAND.orangeDark, transform: "translateY(-1px)" },
                }}
              >
                {isAuthenticated ? "Ver mi enlace personal" : referralCode ? "Crear cuenta del negocio" : joinLabel}
              </Button>
              <Button
                variant="outlined"
                size="large"
                startIcon={<WhatsAppIcon />}
                onClick={() => {
                  window.location.href = WHATSAPP_URL;
                }}
                sx={{
                  minHeight: { xs: 46, sm: 54 },
                  px: { xs: 2.2, sm: 2.7 },
                  borderRadius: 999,
                  color: BRAND.dark,
                  borderColor: "rgba(25,36,47,.2)",
                  textTransform: "none",
                  fontWeight: 850,
                  "&:hover": { borderColor: BRAND.dark, bgcolor: "rgba(25,36,47,.035)" },
                }}
              >
                Consultar al equipo
              </Button>
            </Stack>

            <Typography sx={{ mt: { xs: 1, sm: 1.6 }, color: "rgba(25,36,47,.48)", fontSize: ".72rem", lineHeight: 1.4 }}>
              El programa no garantiza ingresos: las comisiones dependen de pagos reales y elegibles.
            </Typography>
          </Box>

          <CommissionCard program={data?.affiliateProgram} loading={loading} />
        </Box>

        <SellerGuideNavigation />

        {error ? (
          <Alert
            severity="warning"
            role="status"
            action={(
              <Button
                color="inherit"
                size="small"
                startIcon={<RefreshRoundedIcon />}
                onClick={() => setRequestVersion((value) => value + 1)}
                sx={{ fontWeight: 900 }}
              >
                Reintentar
              </Button>
            )}
            sx={{ mt: 2, borderRadius: 3.5, alignItems: "center" }}
          >
            <Typography sx={{ fontWeight: 900 }}>Datos comerciales temporalmente no disponibles.</Typography>
            <Typography sx={{ mt: 0.25, fontSize: ".82rem", lineHeight: 1.5 }}>
              {error} La guía general sigue visible, pero no se muestran precios ni porcentajes alternativos.
            </Typography>
          </Alert>
        ) : null}

        <ReferralJourneyCard
          isAuthenticated={isAuthenticated}
          authLoading={authLoading}
          referralCode={referralCode}
          onJoinClick={onJoinClick}
        />

        <Box id="vendedores-servicios" sx={{ mt: { xs: 3.8, sm: 5, md: 7 }, scrollMarginTop: 96 }}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", md: "flex-end" }}
            spacing={2}
          >
            <Box sx={{ maxWidth: 720 }}>
              <Typography sx={{ color: BRAND.orange, fontWeight: 900, letterSpacing: ".1em", textTransform: "uppercase", fontSize: ".76rem" }}>
                Servicios y precios
              </Typography>
              <Typography sx={{ mt: 1, color: BRAND.dark, fontWeight: 950, letterSpacing: "-.04em", fontSize: { xs: "1.5rem", sm: "2rem", md: "2.7rem" }, lineHeight: 1.08 }}>
                Consultá qué servicios y precios están vigentes.
              </Typography>
              <Typography sx={{ mt: { xs: .8, sm: 1.4 }, color: "rgba(25,36,47,.64)", lineHeight: { xs: 1.45, sm: 1.65 }, fontSize: { xs: ".84rem", sm: "1rem" } }}>
                Usá este catálogo como referencia comercial; cada negocio puede contratar la combinación que corresponda.
              </Typography>
            </Box>
            {pricing?.status === "partial" ? (
              <Chip label="Catálogo parcial disponible" sx={{ bgcolor: "#FFF3D6", color: "#805A00", fontWeight: 850 }} />
            ) : null}
          </Stack>

          {loading ? (
            <Stack direction="row" alignItems="center" spacing={1.2} sx={{ mt: 3.5, minHeight: 120 }}>
              <CircularProgress size={25} sx={{ color: BRAND.orange }} />
              <Typography sx={{ color: "rgba(25,36,47,.6)", fontWeight: 750 }}>
                Cargando servicios y precios vigentes…
              </Typography>
            </Stack>
          ) : services.length ? (
            <>
              <Box
                sx={{
                  mt: { xs: 2, sm: 3.5 },
                  display: "grid",
                  gridTemplateColumns: { xs: "repeat(2,minmax(0,1fr))", sm: "repeat(2,minmax(0,1fr))", lg: "repeat(4,minmax(0,1fr))" },
                  gap: { xs: .8, sm: 2 },
                }}
              >
                {services.map((service) => (
                  <ServiceCard key={service.code || service.name} service={service} />
                ))}
              </Box>
              {hiddenServices ? (
                <Typography sx={{ mt: 1.2, color: "rgba(25,36,47,.5)", fontSize: ".76rem", textAlign: "right" }}>
                  Y {hiddenServices} {hiddenServices === 1 ? "servicio adicional" : "servicios adicionales"} disponible{hiddenServices === 1 ? "" : "s"} en el catálogo vigente.
                </Typography>
              ) : null}
            </>
          ) : (
            <Paper
              elevation={0}
              sx={{ mt: 3.5, p: 3, borderRadius: 4, border: "1px solid rgba(25,36,47,.09)", bgcolor: "#fff" }}
            >
              <Typography sx={{ color: BRAND.dark, fontWeight: 900 }}>
                Los servicios se configuran según cada negocio.
              </Typography>
              <Typography sx={{ mt: 0.8, color: "rgba(25,36,47,.62)", lineHeight: 1.6 }}>
                El catálogo público no está disponible en este momento. Antes de presentar precios o condiciones, consultá al equipo de WebProfit para trabajar con información vigente.
              </Typography>
            </Paper>
          )}
        </Box>

        <SellerIncomeSimulator
          services={pricing?.services}
          program={data?.affiliateProgram}
          loading={loading}
        />

        <SellerProgramDocumentation
          program={data?.affiliateProgram}
          pricing={pricing}
          loading={loading}
          isAuthenticated={isAuthenticated}
          onJoinClick={onJoinClick}
        />

        <Paper
          elevation={0}
          sx={{
            mt: { xs: 3.5, md: 5.5 },
            p: { xs: 2.3, sm: 3.3 },
            display: { xs: "none", sm: "block" },
            borderRadius: 4.5,
            border: "1px solid rgba(42,157,103,.18)",
            bgcolor: "#F0FAF5",
          }}
        >
          <Stack direction={{ xs: "column", sm: "row" }} alignItems={{ xs: "flex-start", sm: "center" }} spacing={2}>
            <Box sx={{ width: 48, height: 48, flexShrink: 0, borderRadius: 3, bgcolor: BRAND.green, color: "#fff", display: "grid", placeItems: "center" }}>
              <HandshakeRoundedIcon />
            </Box>
            <Box sx={{ flexGrow: 1 }}>
              <Typography sx={{ color: BRAND.dark, fontWeight: 950, fontSize: "1.08rem" }}>
                La prioridad es que el negocio elija bien, no venderle de más.
              </Typography>
              <Typography sx={{ mt: 0.55, color: "rgba(25,36,47,.64)", lineHeight: 1.6 }}>
                Recomendá sólo los servicios que aportan valor y usá siempre los precios y condiciones visibles en la plataforma.
              </Typography>
            </Box>
            <Button
              onClick={isAuthenticated
                ? () => document.getElementById("mi-enlace-referidos")?.scrollIntoView({ behavior: "smooth", block: "center" })
                : onJoinClick}
              variant="contained"
              endIcon={<ArrowForwardRoundedIcon />}
              sx={{
                flexShrink: 0,
                minHeight: 50,
                px: 2.6,
                borderRadius: 999,
                bgcolor: BRAND.dark,
                textTransform: "none",
                fontWeight: 900,
                "&:hover": { bgcolor: "#0F1922" },
              }}
            >
              {isAuthenticated ? "Ver mi enlace personal" : referralCode ? "Crear cuenta del negocio" : "Empezar como referente"}
            </Button>
          </Stack>
        </Paper>
      </Container>
    </Box>
  );
}
